<template>
  <DialogRoot v-model:open="dialogOpen" @update:open="handleCloseAIChatModal">
    <DialogPortal>
      <Transition enter-active-class="backdrop-enter" leave-active-class="backdrop-leave">
        <DialogOverlay v-if="dialogOpen" class="modal-backdrop" />
      </Transition>

      <Transition enter-active-class="modal-enter" leave-active-class="modal-leave">
        <DialogContent v-if="dialogOpen" class="modal-content">
          <DialogTitle as="div" class="mb-6 flex justify-between items-start flex-shrink-0">
            <div class="flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M10.8358 13.7107L9.33582 17.781C9.29143 17.9007 9.21149 18.0038 9.10674 18.0767C9.002 18.1495 8.87747 18.1886 8.74989 18.1886C8.6223 18.1886 8.49777 18.1495 8.39303 18.0767C8.28829 18.0038 8.20835 17.9007 8.16395 17.781L6.66395 13.7107C6.63229 13.6248 6.58237 13.5468 6.51763 13.4821C6.45288 13.4173 6.37486 13.3674 6.28895 13.3357L2.21864 11.8357C2.09903 11.7913 1.99587 11.7114 1.92302 11.6067C1.85017 11.5019 1.81113 11.3774 1.81113 11.2498C1.81113 11.1222 1.85017 10.9977 1.92302 10.8929C1.99587 10.7882 2.09903 10.7083 2.21864 10.6639L6.28895 9.16386C6.37486 9.1322 6.45288 9.08228 6.51763 9.01754C6.58237 8.95279 6.63229 8.87477 6.66395 8.78886L8.16395 4.71854C8.20835 4.59893 8.28829 4.49578 8.39303 4.42293C8.49777 4.35008 8.6223 4.31104 8.74989 4.31104C8.87747 4.31104 9.002 4.35008 9.10674 4.42293C9.21149 4.49578 9.29143 4.59893 9.33582 4.71854L10.8358 8.78886C10.8675 8.87477 10.9174 8.95279 10.9821 9.01754C11.0469 9.08228 11.1249 9.1322 11.2108 9.16386L15.2811 10.6639C15.4007 10.7083 15.5039 10.7882 15.5768 10.8929C15.6496 10.9977 15.6886 11.1222 15.6886 11.2498C15.6886 11.3774 15.6496 11.5019 15.5768 11.6067C15.5039 11.7114 15.4007 11.7913 15.2811 11.8357L11.2108 13.3357C11.1249 13.3674 11.0469 13.4173 10.9821 13.4821C10.9174 13.5468 10.8675 13.6248 10.8358 13.7107Z"
                  stroke="#37B5AA"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path d="M13.75 1.25V5" stroke="#37B5AA" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M15.625 3.125H11.875" stroke="#37B5AA" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M17.5 5.625V8.125" stroke="#37B5AA" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M18.75 6.875H16.25" stroke="#37B5AA" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <h4 class="">{{ t('components.aichatModal.title') }}</h4>
            </div>

            <DialogClose asChild class="cursor-pointer size-6 min-w-6 mt-1">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" class="text-accent">
                <path
                  d="M16.192 6.34424L11.949 10.5862L7.70697 6.34424L6.29297 7.75824L10.535 12.0002L6.29297 16.2422L7.70697 17.6562L11.949 13.4142L16.192 17.6562L17.606 16.2422L13.364 12.0002L17.606 7.75824L16.192 6.34424Z"
                  fill="currentColor"
                />
              </svg>
            </DialogClose>
          </DialogTitle>

          <DialogDescription class="sr-only">
            {{ t('components.aichatModal.description') }}
          </DialogDescription>

          <!-- Description -->
          <div class="text-regular-normal mb-6 flex-shrink-0">
            {{ t('components.aichatModal.description') }}
          </div>

          <!-- Messages Area - Scrollable -->
          <div ref="messagesContainer" class="messages flex flex-col gap-6 overflow-y-auto mb-6" :class="messages.length > 0 ? 'flex-1 min-h-0' : 'flex-shrink-0'">
            <div
              v-for="(message, index) in messages"
              :key="index"
              class="message flex flex-row gap-4 lg:gap-6 p-2 bg-neutral-lightest"
              :class="{ 'flex-row-reverse': message.type === 'ai' }"
            >
              <svg class="size-6 min-w-6" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 18V21.766L11.277 18H16C17.103 18 18 17.103 18 16V8C18 6.897 17.103 6 16 6H4C2.897 6 2 6.897 2 8V16C2 17.103 2.897 18 4 18H5ZM4 8H16V16H10.723L7 18.234V16H4V8Z"
                  fill="#37B5AA"
                />
                <path d="M20 2H8C6.897 2 6 2.897 6 4H18C19.103 4 20 4.897 20 6V14C21.103 14 22 13.103 22 12V4C22 2.897 21.103 2 20 2Z" fill="#37B5AA" />
              </svg>
              <div class="message-content text-regular-normal">
                <!-- The server's degraded message already explains itself, so style it rather than add a second notice. -->
                <div
                  class="text-regular-normal inner-html-text"
                  :class="{ 'degraded-notice': message.degraded }"
                  :role="message.degraded ? 'status' : undefined"
                  v-html="message.content"
                ></div>
                <div v-if="message.recommendedVehicles && message.recommendedVehicles.length > 0" class="mt-3">
                  <p class="text-sm font-medium text-gray-700 mb-2">
                    {{ t('components.aichatModal.recommendedVehicles') }}
                  </p>
                  <ul class="space-y-2 list-disc list-inside pl-3">
                    <li v-for="vehicle in message.recommendedVehicles" :key="vehicle.motiondata_id">
                      <NuxtLink :to="getLocaleVehicleUrl(vehicle as unknown as Vehicle)" @click="handleCloseAIChatModal(false)" class="text-regular-link">
                        {{ vehicle.make.name }} {{ vehicle.model }}
                      </NuxtLink>
                    </li>
                  </ul>
                </div>
                <div v-if="message.alternativeVehicles && message.alternativeVehicles.length > 0" class="mt-3">
                  <p class="text-sm font-medium text-gray-700 mb-2">
                    {{ t('components.aichatModal.alternativeVehicles') }}
                  </p>
                  <ul class="space-y-2 list-disc list-inside pl-3">
                    <li v-for="vehicle in message.alternativeVehicles" :key="vehicle.motiondata_id">
                      <NuxtLink :to="getLocaleVehicleUrl(vehicle as unknown as Vehicle)" @click="handleCloseAIChatModal(false)" class="text-regular-link">
                        {{ vehicle.make.name }} {{ vehicle.model }}
                      </NuxtLink>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Typing Indicator -->
            <div v-if="isTyping" class="message flex flex-row justify-between gap-4 lg:gap-6 p-2 bg-neutral-lightest">
              <div class="message-content text-regular-normal">
                <div class="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
              <svg class="size-6 min-w-6" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 18V21.766L11.277 18H16C17.103 18 18 17.103 18 16V8C18 6.897 17.103 6 16 6H4C2.897 6 2 6.897 2 8V16C2 17.103 2.897 18 4 18H5ZM4 8H16V16H10.723L7 18.234V16H4V8Z"
                  fill="#37B5AA"
                />
                <path d="M20 2H8C6.897 2 6 2.897 6 4H18C19.103 4 20 4.897 20 6V14C21.103 14 22 13.103 22 12V4C22 2.897 21.103 2 20 2Z" fill="#37B5AA" />
              </svg>
            </div>
          </div>

          <!-- Form - Fixed at bottom -->
          <form class="flex flex-col gap-6 flex-shrink-0" @submit.prevent="handleSubmit">
            <SharedInput
              :id="`${compoentId}-message`"
              name="message"
              type="text"
              hide-label
              :error="messageError"
              :model-value="messageValue"
              @update:model-value="setMessageValue"
              :placeholder="t('components.aichatModal.placeholder')"
            />
            <SharedCheckbox
              :id="`${compoentId}-acceptTerms`"
              name="acceptTerms"
              :model-value="!!acceptTerms"
              required
              @update:modelValue="setFieldValue('acceptTerms', $event)"
              :error="errors.acceptTerms"
            >
              <i18n-t keypath="components.newsletterSignupForm.acceptTerms" scope="global">
                <NuxtLink
                  class="text-regular-normal !font-bold underline"
                  :target="'_blank'"
                  rel="noopener noreferrer"
                  :to="`${privacyPolicyUrl}`"
                  :title="siteOptions?.data?.dataProtectionPolicy?.title"
                >
                  {{ t('components.contactForm.acceptTermsLink') }}
                </NuxtLink>
              </i18n-t>
            </SharedCheckbox>

            <div class="flex items-center justify-between">
              <SharedButton :label="t('components.aichatModal.clearButton')" @click="closeAIChatModal" variant="secondary" class="w-max h-min" />

              <SharedButton :label="t('components.aichatModal.button')" :disabled="isSubmitButtonDisabled" :loading="isSubmitting" type="submit" class="ml-auto w-max h-min" />
            </div>

            <p class="text-regular-normal">
              <b>{{ t('components.aichatModal.disclaimerWarn') }}</b> {{ t('components.aichatModal.disclaimerText') }}
            </p>

            <NuxtTurnstile v-model="turnstileToken" v-if="!validTurnstileToken" />
          </form>
        </DialogContent>
      </Transition>
    </DialogPortal>
  </DialogRoot>
</template>

<script setup lang="ts">
  const compoentId = useId();

  import { useForm } from 'vee-validate';
  import { z } from 'zod';
  import { toTypedSchema } from '@vee-validate/zod';
  import { DialogClose, DialogContent, DialogDescription, DialogOverlay, DialogPortal, DialogRoot, DialogTitle } from 'reka-ui';
  import type { Vehicle, StrapiResponse, SiteOption } from '~/types/strapi';
  import type { RecommendedVehicle } from '~/types/api';
  import type { ChatResponse } from '~/server/api/ai/ai-chat.post';

  interface EventData {
    event: 'aichat_modal_opened' | 'aichat_modal_closed' | 'aichat_message_sent';
    page_location: string;
  }

  interface Message {
    type: 'user' | 'ai';
    content: string;
    recommendedVehicles?: RecommendedVehicle[];
    alternativeVehicles?: RecommendedVehicle[];
    /** Search succeeded but the assistant text failed; the vehicles are still valid. */
    degraded?: boolean;
  }

  const dialogOpen = ref(false);

  const { t } = useI18n();
  const { lockScroll, unlockScroll } = useScrollLock();
  const eventBus = useEventBus();

  const schema = toTypedSchema(
    z.object({
      message: z.string().refine(val => val.trim().length > 0, {
        message: t('validation.required'),
      }),
      acceptTerms: z.boolean().refine(val => val, t('validation.required')),
    })
  );

  const {
    values,
    errors,
    meta,
    setFieldValue,
    setFieldTouched,
    handleSubmit: submitForm,
    isSubmitting,
    defineField,
    resetForm,
  } = useForm({
    validationSchema: schema,
    initialValues: {
      message: '',
    },
  });

  const siteOptions = inject('siteOptions') as Ref<StrapiResponse<SiteOption>> | undefined;
  const privacyPolicyUrl = computed(() => useLocalizedPath(siteOptions?.value?.data?.dataProtectionPolicy?.slug || ''));
  const [acceptTerms, acceptTermsAttrs] = defineField('acceptTerms');

  const messageValue = computed(() => values.message || '');
  const messageError = computed(() => {
    return meta.value.touched && errors.value.message ? errors.value.message : undefined;
  });
  const isFormValid = computed(() => messageValue.value.trim().length > 0 && acceptTerms.value);

  const isSubmitButtonDisabled = computed(() => isSubmitting.value || !validTurnstileToken.value || !isFormValid.value);

  const setMessageValue = (value: string | number) => {
    setFieldValue('message', String(value));
  };

  const closeAIChatModal = () => {
    resetForm();
    handleCloseAIChatModal(false);
  };

  const { push } = useDataLayer();

  const handleOpenAIChatModal = (initialMessage?: string) => {
    if (!dialogOpen.value) {
      const eventData: EventData = {
        event: 'aichat_modal_opened',
        page_location: window.location.href,
      };
      push(eventData);
    }

    dialogOpen.value = true;
    lockScroll();
    if (initialMessage) {
      setFieldValue('message', initialMessage, false);
      setFieldTouched('message', initialMessage.length > 0);
    }
  };

  const handleCloseAIChatModal = (v: boolean) => {
    if (!v) {
      dialogOpen.value = false;
      unlockScroll();
    }
  };

  eventBus.on('open-aichat-modal', handleOpenAIChatModal);

  const messages = ref<Message[]>([]);
  const messagesContainer = ref<HTMLElement>();
  const isTyping = ref(false);

  const scrollToBottom = (isInstant: boolean = false) => {
    nextTick(() => {
      if (messagesContainer.value) {
        if (isInstant) {
          messagesContainer.value.scrollTo({
            top: messagesContainer.value.scrollHeight,
            behavior: 'instant',
          });
        } else {
          // Scroll to show the top of the last message/typing indicator
          const lastMessage = messagesContainer.value.lastElementChild;
          if (lastMessage) {
            lastMessage.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          } else {
            // Fallback to bottom scroll
            messagesContainer.value.scrollTo({
              top: messagesContainer.value.scrollHeight,
              behavior: 'smooth',
            });
          }
        }
      }
    });
  };

  const { validTurnstileToken, turnstileToken } = useValidTurnstileTokenCookie();

  const previousData = ref<ChatResponse['rawSearchData'] | null>(null);

  const sendMessage = async (message: string) => {
    if (!message.trim()) return;
    if (!validTurnstileToken.value) return;
    if (isSubmitting.value) return;
    if (!isFormValid.value) return;

    // Add user message
    messages.value.push({
      type: 'user',
      content: message.trim(),
    });

    resetForm({
      values: {
        message: '',
        acceptTerms: true,
      },
    });
    isSubmitting.value = true;
    isTyping.value = true;

    const contexts = Array.from(messagesContainer.value?.querySelectorAll('.context-summary') || [])
      .map(context => context.textContent)
      .join('\n');

    try {
      const response = await $fetch<ChatResponse>('/api/ai/ai-chat', {
        method: 'POST',
        body: {
          message: message.trim(),
          langCode: 'de', // TODO: Make this dynamic based on i18n locale
          context: contexts,
          previousData: previousData.value || null,
        },
      });

      if (response.success) {
        previousData.value = response?.rawSearchData || null;
        messages.value.push({
          type: 'ai',
          content: response.message,
          recommendedVehicles: response.recommendedVehicles,
          alternativeVehicles: response.alternativeVehicles,
          degraded: response.degraded,
        });
      } else {
        messages.value.push({
          type: 'ai',
          content: response.message || 'Sorry, I encountered an error. Please try again.',
        });
      }
    } catch (error) {
      console.error('Chat error:', error);
      messages.value.push({
        type: 'ai',
        content: "Sorry, I'm having trouble connecting right now. Please try again later.",
      });
    } finally {
      isSubmitting.value = false;
      isTyping.value = false;
      nextTick(() => {
        scrollToBottom();
      });
    }
  };

  const handleSubmit = () => {
    if (!isFormValid.value) return;
    sendMessage(messageValue.value);
  };

  const { getLocaleVehicleUrl } = useCustomLocaleRoute();

  const viewVehicle = (vehicle: RecommendedVehicle) => {
    // Navigate to vehicle details page using slug
    navigateTo(`/vehicles/${vehicle.slug}`);
  };

  // Watch for new messages and auto-scroll
  watch(
    messages,
    () => {
      scrollToBottom();
    },
    { deep: true }
  );

  onMounted(() => {
    scrollToBottom();
  });

  watch(dialogOpen, () => {
    if (dialogOpen.value) {
      scrollToBottom(true);
    }
  });
</script>

<style lang="scss" scoped>
  // Typing Indicator Animation
  .typing-indicator {
    display: flex;
    align-items: center;
    gap: 4px;
    height: 24px;

    span {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: #37b5aa;
      animation: typing 1.4s infinite ease-in-out;

      &:nth-child(1) {
        animation-delay: -0.32s;
      }

      &:nth-child(2) {
        animation-delay: -0.16s;
      }

      &:nth-child(3) {
        animation-delay: 0s;
      }
    }
  }

  @keyframes typing {
    0%,
    80%,
    100% {
      transform: scale(0.8);
      opacity: 0.5;
    }
    40% {
      transform: scale(1);
      opacity: 1;
    }
  }

  .message-content {
    :deep(.context-summary) {
      @apply hidden;
    }
  }

  // Muted on purpose: the vehicle list below is the real answer.
  .degraded-notice {
    @apply border-l-2 border-neutral-light pl-3 text-neutral-dark;
  }
</style>
